<script setup>
import { ref, onMounted, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const showGallery = ref(false);
const blobUrl = ref(null);
const isLoading = ref(true);
const hasError = ref(false);

const { filteredCurrentChatAttachments } = useMessageContext();

// Fetch video with headers to bypass ngrok warning page in development
const loadVideo = async () => {
  const url = props.attachment?.dataUrl;
  if (!url) {
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    const response = await fetch(url, {
      headers: { 'ngrok-skip-browser-warning': 'true' },
    });

    if (!response.ok) {
      throw new Error('Failed to load video');
    }

    const blob = await response.blob();
    blobUrl.value = URL.createObjectURL(blob);
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadVideo);
watch(() => props.attachment?.dataUrl, loadVideo);
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer relative group"
    @click="showGallery = true"
  >
    <div
      v-if="isLoading"
      class="flex items-center justify-center size-full bg-n-alpha-1"
    >
      <Icon icon="i-lucide-loader-2" class="text-n-slate-11 animate-spin" />
    </div>
    <div
      v-else-if="hasError"
      class="flex items-center justify-center size-full bg-n-alpha-1"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
    </div>
    <template v-else>
      <video
        :src="blobUrl"
        class="w-full h-full object-cover"
        muted
        playsInline
      />
      <div
        class="absolute w-full h-full inset-0 p-1 flex items-center justify-center"
      >
        <div
          class="size-7 bg-n-slate-1/60 backdrop-blur-sm rounded-full overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.4)]"
        >
          <Icon
            icon="i-teenyicons-play-small-solid"
            class="size-7 text-n-slate-12/80 backdrop-blur"
          />
        </div>
      </div>
    </template>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
